import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2, BookOpen, Smile, Mic, Trophy, Flame, TrendingUp } from 'lucide-react';
import axios from 'axios';
import Footer from '@/components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WeeklySummaryPage() {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    try {
      const res = await axios.get(`${API}/summary/weekly`, { headers: getAuthHeader() });
      setSummary(res.data);
    } catch (e) {
      console.error('Could not load weekly summary');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const items = [
    { icon: BookOpen, label: 'Dagboksinlägg', value: summary?.journal_entries || 0, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-950' },
    { icon: Smile, label: 'Humörnoteringar', value: summary?.mood_entries || 0, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-950' },
    { icon: Mic, label: 'Röstinspelningar', value: summary?.voice_recordings || 0, color: 'text-violet-600 dark:text-violet-400', bg: 'bg-violet-50 dark:bg-violet-950' },
    { icon: Trophy, label: 'Program-incheckningar', value: summary?.program_checkins || 0, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950' },
  ];

  const moodLabel = summary?.average_mood 
    ? summary.average_mood >= 4 ? 'Bra' : summary.average_mood >= 3 ? 'Okej' : 'Tufft'
    : null;

  return (
    <div className="min-h-screen bg-background" data-testid="weekly-summary-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-4 flex justify-between items-center">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="rounded-full" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Tillbaka
          </Button>
          <h1 className="font-heading text-xl">Veckosammanfattning</h1>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <div className="text-center mb-8 animate-fade-in">
          <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <TrendingUp className="w-8 h-8 text-primary" />
          </div>
          <h2 className="font-heading text-2xl mb-2">Din vecka</h2>
          <p className="text-muted-foreground text-sm">
            {new Date(summary?.period_start).toLocaleDateString('sv-SE')} — {new Date(summary?.period_end).toLocaleDateString('sv-SE')}
          </p>
        </div>

        {/* Streak highlight */}
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30 rounded-2xl p-6 mb-6 animate-fade-in">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-orange-100 dark:bg-orange-900/40 flex items-center justify-center">
              <Flame className="w-7 h-7 text-orange-500" />
            </div>
            <div>
              <p className="text-2xl font-bold font-heading">{summary?.current_streak || 0} dagar</p>
              <p className="text-sm text-muted-foreground">Nuvarande streak</p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-lg font-bold font-heading">{summary?.longest_streak || 0}</p>
              <p className="text-xs text-muted-foreground">Längsta</p>
            </div>
          </div>
        </div>

        {/* Activity stats */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          {items.map((item, i) => (
            <div
              key={i}
              className="bg-card rounded-2xl p-5 border border-border/30 animate-slide-up"
              style={{ animationDelay: `${i * 0.1}s` }}
              data-testid={`summary-stat-${i}`}
            >
              <div className={`w-10 h-10 rounded-xl ${item.bg} flex items-center justify-center mb-3`}>
                <item.icon className={`w-5 h-5 ${item.color}`} />
              </div>
              <p className="text-2xl font-bold font-heading">{item.value}</p>
              <p className="text-sm text-muted-foreground">{item.label}</p>
            </div>
          ))}
        </div>

        {/* Average mood */}
        {summary?.average_mood && (
          <div className="bg-card rounded-2xl p-6 border border-border/30 text-center animate-fade-in">
            <p className="text-sm text-muted-foreground mb-2">Genomsnittligt humör</p>
            <p className="text-3xl font-bold font-heading">{summary.average_mood}</p>
            <p className="text-sm text-muted-foreground">{moodLabel}</p>
          </div>
        )}

        {/* Active programs */}
        {summary?.active_programs > 0 && (
          <div className="mt-6 bg-card rounded-2xl p-6 border border-border/30 animate-fade-in">
            <p className="text-sm font-medium mb-1">Aktiva program</p>
            <p className="text-2xl font-bold font-heading">{summary.active_programs}</p>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
